import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';

/**
 * GATEHOUSE (Привратная) — the WELCOMING BARBICAN + SPAWN: the first room every player sees. A grand,
 * honeyed-warm arrival hall, NOT a cluttered guardpost. The composition is built as one strong, symmetric
 * tableau around a single hero so a newly-spawned duck instantly knows where to look:
 *
 *   HERO  — the heavy stone PORTCULLIS arch + raised grille on the N edge, a golden guild CREST mounted
 *           over its mouth, a ceremonial welcome RUNNER spilling inward, and a CHANDELIER glowing down it.
 *   GUARD — two SUIT-OF-ARMOUR sentinels flank the gate; two guild BANNERS hang high; two BRAZIERS frame
 *           the portcullis with warm fire (these braziers + the chandelier do most of the lighting).
 *   REST  — two arrival PEWS sit symmetrically against the side walls (at the seat anchors) so a traveller
 *           can rest; a single guest-ledger LECTERN ("sign in, traveller") and a barrel-stack of stores
 *           give the corners just enough lived-in story.
 *
 * Deliberately SPARSE: the central cross + most of the floor is left open (empty space IS the design — a
 * welcome needs room to breathe). Removed the old heap of lamps/extra torches/herald-table/loose barrels
 * that were only "filling the dead half".
 *
 * Geometry: zone (1360,2300) 880×340 → centre (1800,2470). PORTCULLIS on the N edge at x1800 (mouth kept
 * clear). Side doors W@y2470 (cellar) + E@y2470 (treasury) kept clear.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── Floor: a ceremonial welcome runner spilling from the gate mouth inward (laid first, under all). ──
  place(ctx, kit.runnerRug(art, 100, 230), 1800, 2440);

  // ── HERO: the PORTCULLIS arch — heavy stone gate + raised iron grille on the north edge. ──
  place(ctx, kit.portcullisArch(art), 1800, 2300);
  // A golden guild CREST over the inner gate — the unmistakable "you've arrived" beat.
  place(ctx, kit.trophyShield(art, 0xb8842c), 1800, 2345);
  // An overhead CHANDELIER pours warm light straight down the entry runner.
  place(ctx, kit.chandelier(art), 1800, 2455);

  // ── Guild BANNERS hung high + BRAZIERS framing the gate (these do the hero's warm lighting). ──
  place(ctx, kit.banner(art, undefined, 96), 1590, 2330);
  place(ctx, kit.banner(art, 0x2f4a8a, 96), 2010, 2330);
  place(ctx, kit.brazier(art), 1630, 2380);
  place(ctx, kit.brazier(art), 1970, 2380);

  // ── Two SUIT-OF-ARMOUR sentinels standing watch, flanking the gate mouth. ──
  place(ctx, kit.suitOfArmour(art), 1680, 2360);
  place(ctx, kit.suitOfArmour(art), 1920, 2360);

  // ── Arrival PEWS against the side walls (AT the seat anchors — a duck visibly sits to rest). ──
  place(ctx, kit.pew(art, 80), 1450, 2470); // gatehouse-bench-l (west wall)
  place(ctx, kit.pew(art, 80), 2150, 2470); // gatehouse-bench-r (east wall)

  // ── A pair of low wall-torches warm the resting pews (the only added glow — symmetric, restrained). ──
  place(ctx, kit.wallTorch(art), 1450, 2580);
  place(ctx, kit.wallTorch(art), 2150, 2580);

  // ── Just enough lived-in story in the corners: the guest-ledger lectern + travellers' stores. ──
  place(ctx, kit.lectern(art), 1560, 2580); // sign in, traveller (SW)
  place(ctx, kit.barrelStack(art), 2120, 2580); // travellers' stores (SE)
};

export default decorate;
